import React from 'react';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Users, Wifi, WifiOff } from 'lucide-react';

const PresenceBar = ({ presence = [], isConnected = false, className = '' }) => {
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusColor = (user) => {
    const lastSeen = new Date(user.last_seen);
    const now = new Date();
    const diffMinutes = (now - lastSeen) / (1000 * 60);
    
    if (diffMinutes < 2) return 'bg-green-500'; // Active
    if (diffMinutes < 10) return 'bg-yellow-500'; // Away
    return 'bg-gray-400'; // Inactive
  };

  const formatLastSeen = (lastSeen) => {
    const now = new Date();
    const diff = now - new Date(lastSeen);
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return 'Yesterday';
  };

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {/* Connection Status */}
      <div className="flex items-center space-x-2">
        {isConnected ? (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <Wifi className="w-3 h-3 mr-1" />
            Live
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <WifiOff className="w-3 h-3 mr-1" />
            Offline
          </Badge>
        )}
      </div>

      {/* Presence Count */}
      {presence.length > 0 && (
        <div className="flex items-center space-x-1 text-sm text-gray-600">
          <Users className="w-4 h-4" />
          <span>{presence.length} online</span>
        </div>
      )}

      {/* User Avatars */}
      <div className="flex -space-x-2">
        <TooltipProvider>
          {presence.slice(0, 5).map((user) => (
            <Tooltip key={user.user_id}>
              <TooltipTrigger>
                <div className="relative">
                  <Avatar className="w-8 h-8 border-2 border-white hover:z-10 transition-transform hover:scale-110">
                    <AvatarFallback className="text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                      {getInitials(user.user_name)}
                    </AvatarFallback>
                  </Avatar>
                  {/* Status indicator */}
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${getStatusColor(user)}`}></div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-center">
                  <div className="font-medium">{user.user_name}</div>
                  <div className="text-xs text-gray-500">
                    {formatLastSeen(user.last_seen)}
                  </div>
                  {user.active_task_id && (
                    <div className="text-xs text-blue-600 mt-1">
                      Working on task
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          ))}
          
          {/* Show overflow count */}
          {presence.length > 5 && (
            <Tooltip>
              <TooltipTrigger>
                <Avatar className="w-8 h-8 border-2 border-white bg-gray-100">
                  <AvatarFallback className="text-xs text-gray-600">
                    +{presence.length - 5}
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent>
                <div>
                  <div className="font-medium">More users online:</div>
                  {presence.slice(5).map(user => (
                    <div key={user.user_id} className="text-sm">
                      {user.user_name}
                    </div>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </TooltipProvider>
      </div>
    </div>
  );
};

export default PresenceBar;